/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
  ----------------------------------------------------------------------------------------
  Item Name: Vuesax Admin - VueJS Dashboard Admin Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/


import Vue from 'vue'
import Router from 'vue-router'
import auth from "@/auth/authService";

import firebase from 'firebase/app'
import 'firebase/auth'
import TCVelikClient from '@/TCVelikClient/TCVelikClient.js';
import AccountsService from '@/accounts/accountsService.js';
import accountsService from './accounts/accountsService';
import analyticsService from './services/analyticsService';

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return {
      x: 0,
      y: 0
    }
  },
  routes: [
    {
      path: '/',
      name: 'home',
      redirect: '/pages/home',
      meta: {
        rule: 'guest',
        isPublic: true
      }
    },
    // =============================================================================
    // MAIN LAYOUT ROUTES
    // =============================================================================
    {
      path: '',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        // =============================================================================
        // Theme Routes
        // =============================================================================
        {
          path: '/pages/home',
          name: 'homePage',
          component: () => import('@/views/OnboardingList'),
          meta: {
            rule: 'guest',
            isPublic: false,
            title: 'Discover & Improve'
          },
        },

        // Form Routes
        {
          path: '/pages/forms',
          name: 'formsIndex',
          component: () => import('@/views/forms/FormIndex.vue'),
          meta: {
            rule: 'editor',
            isPublic: false,
            title: 'Forms'
          },
        },
        {
          path: '/pages/forms/layouts',
          name: 'formLayouts',
          component: () => import('@/views/forms/FormLayouts.vue'),
          meta: {
            rule: 'editor',
            isPublic: false,
            title: 'Form Layouts'
          },
        },
        {
          path: '/pages/forms/validation',
          name: 'formValidation',
          component: () => import('@/views/forms/form-validation/FormValidation.vue'),
          meta: {
            rule: 'editor',
            isPublic: false,
            title: 'Form Validation'
          },
        },
        {
          path: '/pages/forms/wizard',
          name: 'formWizard',
          component: () => import('@/views/forms/form-wizard/FormWizard.vue'),
          meta: {
            rule: 'editor',
            isPublic: false,
            title: 'Form Wizard'
          },
        },
        {
          path: '/pages/forms/input-group',
          name: 'formInputGroup',
          component: () => import('@/views/forms/form-input-group/FormInputGroup.vue'),
          meta: {
            rule: 'editor',
            isPublic: false,
            title: 'Form Input Groups'
          },
        },
        {
          path: '/pages/forms/modals',
          name: 'formModals',
          component: () => import('@/views/forms/FormModals.vue'),
          meta: {
            rule: 'editor',
            isPublic: false,
            title: 'Form Modals'
          },
        },
        {
          path: '/pages/forms/app-forms',
          name: 'appForms',
          component: () => import('@/views/forms/AppForms.vue'),
          meta: {
            rule: 'editor',
            isPublic: false,
            title: 'Application Forms'
          },
        },
        {
          path: '/pages/forms/builder',
          name: 'formBuilder',
          component: () => import('@/views/forms/FormBuilder.vue'),
          meta: {
            rule: 'editor',
            isPublic: false,
            title: 'Form Builder'
          },
        },

        // ProcessRun Routes
        //{
        //  path: '/pages/process-runs',
        //  name: 'processRuns',
        //  component: () => import('@/views/ProcessRuns.vue'),
        //  meta: {
        //    rule: 'editor',
        //    isPublic: false,
        //    title: 'Process Runs'
        //  },
        //},

        //// ProcessTemplate Routes
        //{
        //  path: '/pages/process-templates',
        //  name: 'processTemplates',
        //  component: () => import('@/views/ProcessTemplates.vue'),
        //  meta: {
        //    rule: 'editor',
        //    isPublic: false,
        //    title: 'Process Templates'
        //  },
        //},
        //{
        //  path: '/pages/process-executions',
        //  name: 'processExecutions',
        //  component: () => import('@/views/ProcessExecutions.vue'),
        //  meta: {
        //    rule: 'editor',
        //    isPublic: false,
        //    title: 'Process Executions'
        //  },
        //},
        //{
        //  path: '/pages/process-executions/:executionId',
        //  name: 'processExecutionDetail',
        //  component: () => import('@/views/ProcessExecutions.vue'),
        //  meta: {
        //    rule: 'editor',
        //    isPublic: false,
        //    title: 'Process Execution Detail'
        //  },
        //},

        {
          path: '/pages/liveguide/:onboardingid',
          name: 'onboardingsPage',
          component: () => import('@/views/SideBarGuide.vue'),
          meta: {
            rule: 'guest',
            isPublic: false,
            title: 'Onboarding Guide', // Add title to prevent router guard from overriding component-set title
            isAsync: true // Component will signal when analytics should track
          },
        },
        {
          path: '/pages/analyticsOverview',
          name: 'pageAnalyticsOverview',
          component: () => import('@/views/pages/OnboardingAnalytics.vue'),
          meta: {
            rule: 'admin',
            isPublic: false,
            title: 'Analytics Overview'
          },
        },
        {
          path: '/components/button',
          name: 'componentButton',
          component: () => import('@/views/components/vuesax/button/Button.vue'),
          meta: {
            breadcrumb: [{
              title: 'Home',
              url: '/'
            },
            {
              title: 'Components'
            },
            {
              title: 'Button',
              active: true
            },
            ],
            pageTitle: 'Button',
            rule: 'editor'
          },
        },
        {
          path: '/pages/analytics/:slug',
          name: 'pageAnalytics',
          component: () => import('@/views/pages/OnboardingAnalytics.vue'),
          meta: {
            rule: 'admin',
            isPublic: false
          },
        },
        {
          path: '/pages/calendar',
          name: 'pageSimpleCalendar',
          component: () => import('@/views/apps/calendar/SimpleCalendar.vue'),
          meta: {
            rule: 'guest',
            isPublic: false,
            breadcrumb: [{
              title: 'Home',
              url: '/'
            }, {
              title: 'Calendar',
              active: true
            }],
            pageTitle: 'Calendar'
          },
        },
        {
          path: '/pages/fullcalendar',
          name: 'pageFullCalendar',
          component: () => import('@/views/apps/calendar/FullCalendar.vue'),
          meta: {
            rule: 'guest',
            isPublic: false,
            breadcrumb: [{
              title: 'Home',
              url: '/'
            }, {
              title: 'Full Calendar',
              active: true
            }],
            pageTitle: 'Full Calendar'
          },
        },
        {
          path: '/pages/todo',
          name: 'pageTodo',
          component: () => import('@/views/apps/todo/Todo.vue'),
          meta: {
            rule: 'guest',
            isPublic: false,
            breadcrumb: [{
              title: 'Home',
              url: '/'
            }, {
              title: 'Todo',
              active: true
            }],
            pageTitle: 'Todo App'
          },
        },
        {
          path: '/pages/space/:spaceid',
          name: 'spacesDetailsPage',
          component: () => import('@/views/Spaces.vue'),
          meta: {
            rule: 'guest',
            isPublic: true,
            title: 'Space', // Add title to prevent router guard from overriding component-set title
            isAsync: true // Component will signal when analytics should track
          },
        },
        {
          path: '/pages/spaces/manage',
          name: 'spaceManagePage',
          component: () => import('@/views/SpacesManage.vue'),
          meta: {
            rule: 'editor',
            isPublic: false,
            title: 'Manage Spaces'
          },
        },
        {
          path: '/admin/accounts',
          name: 'adminAccounts',
          component: () => import('@/views/admin/AdminControlPanel'),
          meta: {
            rule: 'admin',
            isPublic: false
          },
        },
        {
          path: '/admin/branding',
          name: 'adminBranding',
          component: () => import('@/views/admin/AccountBranding.vue'),
          meta: {
            rule: 'admin',
            isPublic: false
          },
        },
        {
          path: '/account/users',
          name: 'users',
          component: () => import('./views/account/users/Users.vue'),
          meta: {
            rule: 'admin',
            title: 'Users'
          }
        },
        {
          path: '/pages/signals',
          name: 'signalsView',
          component: () => import('@/views/pages/SignalsView.vue'),
          meta: {
            rule: 'admin',
            title: 'Signals Management',
            breadcrumb: [{
              title: 'Home',
              url: '/'
            }, {
              title: 'Signals Management',
              active: true
            }],
            pageTitle: 'Signals Management'
          }
        },
        {
          path: '/pages/search',
          name: 'searchPage',
          component: () => import('@/views/pages/Search.vue'),
          meta: {
            rule: 'guest',
            isPublic: false,
            title: 'Search'
          },
        },
      ],
    },
    {
      path: '/assistants/generic',
      name: 'assistantGeneric',
      component: () => import('@/views/components/chat/AIChatLogWithFunctions.vue'),
      meta: {
        pageTitle: 'AI Assistant',
        rule: 'guest',
        title: 'AI Assistant',
        hideBreadcrumb: true,
        hideNavbar: false,
      },
    },
    {
      path: '/pages/context-and-onboarding',
      name: 'context-and-onboarding',
      component: () => import('@/views/integrations/EmbedEditorView.vue'),
      meta: {
        pageTitle: 'Alignment',
        rule: 'editor',
        title: 'Alignment',
        hideBreadcrumb: true,
        hideNavbar: false,
      },
    },
    //{
    //  path: '/components/notifications',
    //  name: 'componentNotifications',
    //  component: () => import('@/views/components/vuesax/notifications/Notifications.vue'),
    //  meta: {
    //    breadcrumb: [{
    //      title: 'Home',
    //      url: '/'
    //    },
    //    {
    //      title: 'Components'
    //    },
    //    {
    //      title: 'Notifications',
    //      active: true
    //    },
    //    ],
    //    pageTitle: 'Notifications',
    //    rule: 'editor'
    //  },
    //},
    //{
    //  path: '/components/alert',
    //  name: 'componentNotifications',
    //  component: () => import('@/views/components/vuesax/alert/Alert.vue'),
    //  meta: {
    //    breadcrumb: [{
    //      title: 'Home',
    //      url: '/'
    //    },
    //    {
    //      title: 'Components'
    //    },
    //    {
    //      title: 'Notifications',
    //      active: true
    //    },
    //    ],
    //    pageTitle: 'Notifications',
    //    rule: 'editor'
    //  },
    //},
    //{
    //  path: '/forms/form-elements/switch',
    //  name: 'formElementSwitch',
    //  component: () => import('./views/forms/form-elements/switch/Switch.vue'),
    //  meta: {
    //    breadcrumb: [{
    //      title: 'Home',
    //      url: '/'
    //    },
    //    {
    //      title: 'Form Elements'
    //    },
    //    {
    //      title: 'Switch',
    //      active: true
    //    },
    //    ],
    //    pageTitle: 'Switch',
    //    rule: 'editor'
    //  },
    //},
    //{
    //  path: '/ui-elements/data-list/list-view',
    //  name: 'dataListListView',
    //  component: () => import('@/views/ui-elements/data-list/list-view/DataListListView.vue'),
    //  meta: {
    //    breadcrumb: [{
    //      title: 'Home',
    //      url: '/'
    //    },
    //    {
    //      title: 'Data List'
    //    },
    //    {
    //      title: 'List View',
    //      active: true
    //    },
    //    ],
    //    pageTitle: 'List View',
    //    rule: 'editor'
    //  },
    //},
    {
      path: '/ui-elements/table',
      name: 'table',
      component: () => import('./views/ui-elements/table/Table.vue'),
      meta: {
        breadcrumb: [{
          title: 'Home',
          url: '/'
        },
        {
          title: 'Table',
          active: true
        },
        ],
        pageTitle: 'Table',
        rule: 'editor'
      },
    },
    {
      path: '/components/upload',
      name: 'loaders',
      component: () => import('./views/components/vuesax/upload/Upload.vue'),
      meta: {
        breadcrumb: [{
          title: 'Home',
          url: '/'
        },
        {
          title: 'Table',
          active: true
        },
        ],
        pageTitle: 'Table',
        rule: 'editor'
      },
    },
    //{
    //  path: '/ui-elements/loaders',
    //  name: 'loaders',
    //  component: () => import('./views/components/vuesax/loading/Loading.vue'),
    //  meta: {
    //    breadcrumb: [{
    //      title: 'Home',
    //      url: '/'
    //    },
    //    {
    //      title: 'Table',
    //      active: true
    //    },
    //    ],
    //    pageTitle: 'Table',
    //    rule: 'editor'
    //  },
    //},
    //{
    //  path: '/ui-elements/list',
    //  name: 'list',
    //  component: () => import('./views/components/vuesax/list/List.vue'),
    //  meta: {
    //    breadcrumb: [{
    //      title: 'Home',
    //      url: '/'
    //    },
    //    {
    //      title: 'Table',
    //      active: true
    //    },
    //    ],
    //    pageTitle: 'Table',
    //    rule: 'editor'
    //  },
    //},
    {
      path: '/ui-elements/dropdown',
      name: 'dropdown',
      component: () => import('./views/components/vuesax/dropdown/Dropdown.vue'),
      meta: {
        breadcrumb: [{
          title: 'Home',
          url: '/'
        },
        {
          title: 'Table',
          active: true
        },
        ],
        pageTitle: 'Table',
        rule: 'editor'
      },
    },
    //{
    //  path: '/ui-elements/collapse',
    //  name: 'collapse',
    //  component: () => import('./views/components/vuesax/collapse/Collapse.vue'),
    //  meta: {
    //    breadcrumb: [{
    //      title: 'Home',
    //      url: '/'
    //    },
    //    {
    //      title: 'Table',
    //      active: true
    //    },
    //    ],
    //    pageTitle: 'Table',
    //    rule: 'editor'
    //  },
    //},
    //{
    //  path: '/ui-elements/chip',
    //  name: 'chipDemoPage',
    //  component: () => import('./views/components/vuesax/chip/Chip.vue'),
    //  meta: {
    //    breadcrumb: [{
    //      title: 'Home',
    //      url: '/'
    //    },
    //    {
    //      title: 'Table',
    //      active: true
    //    },
    //    ],
    //    pageTitle: 'Table',
    //    rule: 'editor'
    //  },
    //},
    //{
    //  path: '/ui-elements/navbar',
    //  name: 'navbarDemoPage',
    //  component: () => import('./views/components/vuesax/navbar/Navbar.vue'),
    //  meta: {
    //    breadcrumb: [{
    //      title: 'Home',
    //      url: '/'
    //    },
    //    {
    //      title: 'Table',
    //      active: true
    //    },
    //    ],
    //    pageTitle: 'Table',
    //    rule: 'editor'
    //  },
    //},
    //{
    //  path: '/ui-elements/slider',
    //  name: 'sliderDemoPage',
    //  component: () => import('./views/components/vuesax/slider/Slider.vue'),
    //  meta: {
    //    breadcrumb: [{
    //      title: 'Home',
    //      url: '/'
    //    },
    //    {
    //      title: 'Table',
    //      active: true
    //    },
    //    ],
    //    pageTitle: 'Table',
    //    rule: 'editor'
    //  },
    //},
    {
      path: '/ui-elements/carousel',
      name: 'carousel',
      component: () => import('./views/components/extra-components/carousel/Carousel.vue'),
      meta: {
        breadcrumb: [{
          title: 'Home',
          url: '/'
        },
        {
          title: 'Table',
          active: true
        },
        ],
        pageTitle: 'Table',
        rule: 'editor'
      },
    },
    //{
    //  path: '/ui-elements/autocomplete',
    //  name: 'autocomplete',
    //  component: () => import('./views/components/extra-components/autocomplete/Autocomplete.vue'),
    //  meta: {
    //    breadcrumb: [{
    //      title: 'Home',
    //      url: '/'
    //    },
    //    {
    //      title: 'Table',
    //      active: true
    //    },
    //    ],
    //    pageTitle: 'Table',
    //    rule: 'editor'
    //  },
    //},
    //{
    //  path: '/ui-elements/contextmenu',
    //  name: 'contextmenu',
    //  component: () => import('./views/components/extra-components/context-menu/ContextMenu.vue'),
    //  meta: {
    //    breadcrumb: [{
    //      title: 'Home',
    //      url: '/'
    //    },
    //    {
    //      title: 'Table',
    //      active: true
    //    },
    //    ],
    //    pageTitle: 'Table',
    //    rule: 'editor'
    //  },
    //},
    //{
    //  path: '/ui-elements/localization',
    //  name: 'localization',
    //  component: () => import('./views/components/extra-components/I18n.vue'),
    //  meta: {
    //    breadcrumb: [{
    //      title: 'Home',
    //      url: '/'
    //    },
    //    {
    //      title: 'Table',
    //      active: true
    //    },
    //    ],
    //    pageTitle: 'Table',
    //    rule: 'editor'
    //  },
    //},
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: '',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: '/callback',
          name: 'authCallback',
          component: () => import('@/views/Callback.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/pages/record',
          name: 'recordPage',
          component: () => import('@/views/Onboarding.vue'),
          meta: {
            rule: 'editor',
            isPublic: false,
            inRecordOnly: true,
            title: 'SOP Editor'
          },
        },
        //{
        //  path: '/ui-elements/data-list/thumb-view',
        //  name: 'dataListThumbView',
        //  component: () => import('@/views/ui-elements/data-list/thumb-view/DataListThumbView.vue'),
        //  meta: {
        //    breadcrumb: [{
        //      title: 'Home',
        //      url: '/'
        //    },
        //    {
        //      title: 'Data List'
        //    },
        //    {
        //      title: 'Thumb View',
        //      active: true
        //    },
        //    ],
        //    pageTitle: 'Thumb View',
        //    rule: 'editor'
        //  },
        //},
        //{
        //  path: '/ui-elements/colors',
        //  name: 'colors',
        //  component: () => import('./views/ui-elements/colors/Colors.vue'),
        //  meta: {
        //    breadcrumb: [{
        //      title: 'Home',
        //      url: '/'
        //    },
        //    {
        //      title: 'Colors',
        //      active: true
        //    },
        //    ],
        //    pageTitle: 'Colors',
        //    rule: 'editor'
        //  },
        //},
        //{
        //  path: '/ui-elements/ag-grid-table',
        //  name: 'agGridTable',
        //  component: () => import('./views/ui-elements/ag-grid-table/AgGridTable.vue'),
        //  meta: {
        //    breadcrumb: [{
        //      title: 'Home',
        //      url: '/'
        //    },
        //    {
        //      title: 'agGrid Table',
        //      active: true
        //    },
        //    ],
        //    pageTitle: 'agGrid Table',
        //    rule: 'editor'
        //  },
        //},
        //{
        //  path: '/components/avatar',
        //  name: 'componentAvatar',
        //  component: () => import('@/views/components/vuesax/avatar/Avatar.vue'),
        //  meta: {
        //    breadcrumb: [{
        //      title: 'Home',
        //      url: '/'
        //    },
        //    {
        //      title: 'Components'
        //    },
        //    {
        //      title: 'Avatar',
        //      active: true
        //    },
        //    ],
        //    pageTitle: 'Avatar',
        //    rule: 'editor'
        //  },
        //},
        //{
        //  path: '/ui-elements/grid/tailwind',
        //  name: 'gridTailwind',
        //  component: () => import('@/views/ui-elements/grid/tailwind/GridTailwind.vue'),
        //  meta: {
        //    breadcrumb: [{
        //      title: 'Home',
        //      url: '/'
        //    },
        //    {
        //      title: 'Grid'
        //    },
        //    {
        //      title: 'Tailwind',
        //      active: true
        //    },
        //    ],
        //    pageTitle: 'Tailwind Grid',
        //    rule: 'editor'
        //  },
        //},
        //{
        //  path: '/ui-elements/grid/vuesax',
        //  name: 'gridVuesax',
        //  component: () => import('@/views/ui-elements/grid/vuesax/GridVuesax.vue'),
        //  meta: {
        //    breadcrumb: [{
        //      title: 'Home',
        //      url: '/'
        //    },
        //    {
        //      title: 'Grid'
        //    },
        //    {
        //      title: 'Vuesax',
        //      active: true
        //    },
        //    ],
        //    pageTitle: 'Grid',
        //    rule: 'editor'
        //  },
        //},
        {
          path: '/pages/login',
          name: 'pageLogin',
          component: () => import('@/views/pages/Login.vue'),
          meta: {
            isPublic: true,
            title: 'Login'
          },
          beforeEnter: (to, from, next) => {
            // Allow SSO popup to load login page even if already logged in
            if (
              to.query.redirect_uri &&
              accountsService.getUserFromLocalStorage() != null &&
              localStorage.getItem(location.hostname + "-token") != null
            ) {
              // Allow navigation to login page for SSO popup

              next();
              return;
            }
            // Otherwise, redirect to home if already logged in
            if (accountsService.getUserFromLocalStorage() != null && localStorage.getItem(location.hostname + "-token") != null) {
              return next('/pages/home');
            }
            next();
          },
        },
        {
          path: '/pages/register',
          name: 'pageRegister',
          component: () => import('@/views/pages/RegisterWrapper.vue'),
          meta: {
            rule: 'public',
            isPublic: true,
            requiresAuth: true,
            title: 'Register'
          },
          beforeEnter: (to, from, next) => {
            // Store the return URL in localStorage if it exists
            if (to.query.return) {
              localStorage.setItem('returnUrl', to.query.return);
            }
            next();
          }
        },
        //{
        //  path: '/pages/codeai',
        //  name: 'pageCodeAi',
        //  component: () => import('@/views/CodeAI.vue'),
        //  meta: {
        //    rule: 'editor',
        //    isPublic: true
        //  },
        //},
        //{
        //  path: '/pages/charts',
        //  name: 'chartsDemoPage',
        //  component: () => import('@/views/charts-and-maps/charts/apex-charts/ApexCharts.vue'),
        //  meta: {
        //    rule: 'editor',
        //    isPublic: false
        //  },
        //},
        //{
        //  path: '/pages/cardstats',
        //  name: 'cardStatsDemoPage',
        //  component: () => import('@/views/ui-elements/card/CardStatistics.vue'),
        //  meta: {
        //    rule: 'editor',
        //    isPublic: false
        //  },
        //},
        //{
        //  path: '/pages/dragAndDrop',
        //  name: 'cardCommandPage',
        //  component: () => import('@/views/components/extra-components/drag-and-drop/DragAndDropAnimation.vue'),
        //  meta: {
        //    rule: 'editor',
        //    isPublic: false
        //  },
        //},
        //{
        //  path: '/pages/draggable',
        //  name: 'draggablePage',
        //  component: () => import('@/views/ui-elements/card/CardActions.vue'),
        //  meta: {
        //    rule: 'editor',
        //    isPublic: false
        //  },
        //},
        //{
        //  path: '/pages/e-charts',
        //  name: 'eChartsDemoPage',
        //  component: () => import('@/views/charts-and-maps/charts/echarts/Echarts.vue'),
        //  meta: {
        //    rule: 'editor',
        //    isPublic: false
        //  },
        //},
        {
          path: '/pages/profile',
          name: 'eChartsDemoPage',
          component: () => import('@/views/pages/Profile.vue'),
          meta: {
            rule: 'guest',
            isPublic: false,
            title: 'Profile'
          },
        },
        {
          path: '/pages/profile/:slug',
          name: 'eChartsDemoPage',
          component: () => import('@/views/pages/Profile.vue'),
          meta: {
            rule: 'guest',
            isPublic: false
          },
        },
        {
          path: '/pages',
          name: 'pages',
          redirect: '/pages/login',
          meta: {
            isPublic: true
          },
        },
        {
          path: '/pages/not-authorized',
          name: 'pagesNotAuthorized',
          component: () => import('@/views/pages/NotAuthorized.vue'),
          meta: {
            isPublic: true
          },
        },
        {
          path: '/pages/login-link-verification',
          name: 'loginLinkVerification',
          component: () => import('@/views/verification/LoginLinkVerification.vue'),
          meta: {
            isPublic: true
          },
        },

        {
          path: '/pages/pdf-view',
          name: 'pdfViewPage',
          component: () => import('@/views/OnboardingPDF/OnboardingPDF.vue'),
          meta: {
            rule: 'public',
            isPublic: true
          }
        },
        {
          path: '/pages/all-onboardings',
          name: 'allOnboardingsPage',
          component: () => import('@/views/OnboardingList'),
          meta: {
            rule: 'editor',
            isPublic: false
          },
        },

        {
          path: '/pages/forgot-password',
          name: 'pageForgotPassword',
          component: () => import('@/views/pages/ForgotPassword.vue'),
          meta: {
            isPublic: true
          },
        },
        {
          path: '/pages/reset-password',
          name: 'pageResetPassword',
          component: () => import('@/views/pages/ResetPassword.vue'),
          meta: {
            isPublic: true
          },
        },

        // Licensing Routes
        {
          path: '/licensing/catalog',
          name: 'licensingCatalog',
          component: () => import('@/views/pages/LicensingCatalog.vue'),
          meta: {
            rule: 'guest',
            isPublic: true
          },
        },
        {
          path: '/licensing/callback',
          name: 'paymentCallback',
          component: () => import('@/views/pages/PaymentCallback.vue'),
          meta: {
            rule: 'guest',
            isPublic: true
          },
        },
        {
          path: '/licensing/success',
          name: 'paymentSuccess',
          component: () => import('@/views/pages/PaymentSuccess.vue'),
          meta: {
            rule: 'guest',
            isPublic: true
          },
        },
        {
          path: '/licensing/cancelled',
          name: 'paymentCancelled',
          component: () => import('@/views/pages/PaymentCancelled.vue'),
          meta: {
            rule: 'guest',
            isPublic: true
          },
        },
        {
          path: '/licensing/error',
          name: 'paymentError',
          component: () => import('@/views/pages/PaymentError.vue'),
          meta: {
            rule: 'guest',
            isPublic: true
          },
        },

      ]
    },
    // Redirect to 404 page, if no match found

    {
      path: '*',
      component: () => import('@/views/pages/Error404.vue'),
      meta: {
        rule: 'editor',
        isPublic: true,

      },
    }
  ],
})

router.afterEach((to) => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = "none";
  }

  Vue.nextTick(() => {
    if (!to.meta || !to.meta.isAsync) {
      // Set page title only if it hasn't been set by the component
      // This allows components to override the default title
      const defaultTitle = to.meta.title || "TCVelik - the best place to acquire skills.";
      if (!document.title || document.title === defaultTitle) {
        document.title = defaultTitle;
      }

      // For non-async routes, call seoReady to track analytics
      // This ensures consistent tracking behavior across all routes
      if (typeof window.seoReady === 'function') {
        window.seoReady();
      }
    }
  });

  // Clean up old tcv- classes and apply new ones
  const body = document.getElementsByTagName("body")[0];
  
  // Remove all existing tcv- classes
  Array.from(body.classList).forEach(className => {
    if (className.startsWith('tcv-')) {
      body.classList.remove(className);
    }
  });
  
  // Add new tcv- class and special styles
  if (to && to.meta && to.meta.SpecialStyles) {
    body.classList.add(to.meta.SpecialStyles, 'tcv-' + to.name);
  } else {
    body.classList.add('tcv-' + to.name);
  }
})

router.beforeEach(async (to, from, next) => {
  TCVelikClient.selectUserInfo();
  if (!to.meta.rule && (to.meta.isPublic === true)) { to.meta.rule = 'public'; }
  if (Vue.prototype.$systemSettings) {
    //Update the editor route,so that it's public if the AllowAnonymousEditor setting is true
    if (to.path === '/pages/record' && Vue.prototype.$systemSettings.AllowAnonymousEditor &&
      Vue.prototype.$systemSettings.AllowAnonymousEditor == true) {
      to.meta.rule = 'public'
      to.meta.isPublic = true;
    }


    if (to.path === '/pages/home' && Vue.prototype.$systemSettings.AllowAnonymousEdit &&
      Vue.prototype.$systemSettings.AllowAnonymousHomePage == true) {
      to.meta.rule = 'public'
      to.meta.isPublic = true;
    }
  }


  // Check for magic link parameters first
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  const email = urlParams.get('email');
  const campaignName = urlParams.get('campaignName');

  if (token && email) {
    try {
      // Verify the magic link
      await accountsService.verifyMagicLink(token, campaignName);
      // The verifyMagicLink method will now emit a login event
      // which will be handled by the store's login action
      return;
    } catch (error) {
      console.error('Magic link verification failed:', error);
      // If verification fails, redirect to login
      next({
        path: '/pages/login',
        query: { returnUrl: to.fullPath }
      });
      return;
    }
  }

  // Check if route requires authentication
  const isPublic = to.matched.some(record => record.meta.isPublic);
  const requiresAuth = !isPublic;

  // Check for tokens in URL that need to be processed first
  const urlToken = urlParams.get('token');
  const urlSpaceToken = urlParams.get('spaceToken');

  let isAuthenticated = await AccountsService.isAuthenticated();

  // If we have a token in the URL but aren't authenticated, try to authenticate first
  if ((urlToken || urlSpaceToken) && !isAuthenticated) {
    try {
      console.log('Token found in URL, attempting to authenticate...');
      await AccountsService.refreshUser();
      isAuthenticated = await AccountsService.isAuthenticated();
      console.log('Authentication result after token processing:', isAuthenticated);
    } catch (error) {
      console.error('Failed to authenticate with URL token:', error);
      // Continue with normal flow - the specific pages will handle the token
    }
  }

  // For public routes, create anonymous user if not authenticated
  if (isPublic && !isAuthenticated) {
    try {
      console.log('Public route accessed without authentication, creating anonymous user...');
      await AccountsService.refreshUser();
      isAuthenticated = await AccountsService.isAuthenticated();
      console.log('Anonymous user created, authentication result:', isAuthenticated);
    } catch (error) {
      console.error('Failed to create anonymous user for public route:', error);
      // Continue anyway - public routes should still be accessible
    }
  }

  // Check for first-time setup BEFORE any other authentication logic
  // Skip this check only for specific public routes that should never redirect
  const skipFirstTimeSetupCheck = to.path === '/pages/register' ||
    to.path === '/pages/not-authorized' ||
    to.path === '/pages/login-link-verification' ||
    to.path === '/pages/forgot-password' ||
    to.path === '/pages/reset-password';

  if (!skipFirstTimeSetupCheck) {
    try {
      const isFirstTimeSetup = await accountsService.isFirstTimeSetup();
      if (isFirstTimeSetup) {
        // Redirect to registration for first-time setup, preserving redirect_uri
        const query = { firstTimeSetup: 'true' };
        if (to.query.redirect_uri) {
          query.redirect_uri = to.query.redirect_uri;
        }
        next({
          path: '/pages/register',
          query: query
        });
        return;
      }
    } catch (error) {
      console.error('Error checking first time setup:', error);
      // Continue with normal flow if check fails
    }
  }

  // --- SSO popup support: allow /pages/login?redirect_uri=... to load even if authenticated ---
  if (
    to.path === '/pages/login' &&
    to.query.redirect_uri &&
    isAuthenticated
  ) {
    next(); // allow login page to load for SSO popup
    return;
  }
  // --- END BLOCK ---

  if (requiresAuth && !isAuthenticated) {
    // Store the attempted URL and redirect to register with return URL
    const returnUrl = encodeURIComponent(to.fullPath);
    next({
      path: '/pages/login',
      query: { returnUrl: returnUrl, shortRegistration: '1' }
    });
    return;
  }
  // Check if registration is allowed
  if (to.matched.some(record => record.meta.requiresAuth) &&
    Vue.prototype.$systemSettings.AllowRegistration != true) {
    next('pagesNotAuthorized');
    return;
  }

  // Clean up QR code wrapper if present
  var wrapper = document.querySelector("#qr-wrapper");
  try {
    if (wrapper && wrapper.parentElement.tagName == "BODY") {
      wrapper.remove();
    }
  } catch (err) {
    console.log("Error removing QR Code.");
  }

  // Refresh user data if needed - but only for authenticated users
  if (isAuthenticated) {
    try {
      await AccountsService.refreshUser();
    } catch (err) {
      console.log(err);
    }
  }

  // Notify CefSharp if present
  if (window.chrome && window.chrome.webview && window.chrome.webview.postMessage) {
    window.chrome.webview.postMessage({
      op: "NAVIGATING",
      arg: to.fullPath,
    });
  }

  // Handle special case for context-and-onboarding
  if (from.name == "context-and-onboarding" && to.name == "context-and-onboarding") {
    if ((from.query.projectId || from.query.eid) && (!to.query.projectId && !to.query.eid)) {
      window.location.href = to.fullPath;
      return;
    }
  }

  next();
});

export default router
